import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import ListingCard from '../../components/ListingCard';
import PageHeader from '../../components/ui/PageHeader';
import StatsCard from '../../components/ui/StatsCard';
import Button from '../../components/ui/Button';
import Pagination from '../../components/ui/Pagination';
import useItemFilters from '../../hooks/useItemFilters';
import ItemFilterPanel from '../../components/ItemFilterPanel';

const PAGE_SIZE = 8;

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];
const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'price_asc', label: 'Price ↑' },
  { value: 'price_desc', label: 'Price ↓' },
];

export default function CompanyListings() {
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [page, setPage] = useState(1);

  const {
    filteredItems,
    search, setSearch,
    categoryFilter, setCategoryFilter,
    statusFilter, setStatusFilter,
    sortDir, setSortDir,
    activeFilterCount,
    clearFilters,
  } = useItemFilters(listings);

  const handleClearFilters = () => { clearFilters(); setIsFilterOpen(false); };

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${process.env.REACT_APP_API_URL}/api/listings/my-listings`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => setListings(res.data.listing ?? []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { setPage(1); }, [filteredItems]);

  const activeListings = listings.filter(l => l.status === 'active').length;
  const totalPages = Math.ceil(filteredItems.length / PAGE_SIZE);
  const pagedItems = filteredItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="bg-sand-50 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">

        <PageHeader
          title="My Listings"
          subtitle={`Manage your service listings, ${user?.first_name}`}
          action={
            <Link to="/listings/create">
              <Button variant="primary" size="sm">+ New Listing</Button>
            </Link>
          }
        />

        <div className="grid grid-cols-2 gap-4 mb-8">
          <StatsCard label="Active Listings" value={activeListings} />
          <StatsCard label="Total Listings" value={listings.length} />
        </div>

        {loading ? (
          <div className="flex justify-center py-24">
            <div role="status" aria-label="Loading" className="w-8 h-8 border-2 border-sand-200 border-t-terra-400 rounded-full animate-spin" />
          </div>
        ) : listings.length === 0 ? (
          <div className="flex flex-col items-center py-16 gap-3">
            <p className="font-heading text-lg font-semibold text-ink-900">No listings yet</p>
            <p className="text-sm text-ink-400 mb-2">Post your first service listing to start getting requests</p>
            <Link to="/listings/create"><Button variant="secondary" size="sm">Create Listing</Button></Link>
          </div>
        ) : (
          <>
            <div className="relative mb-3">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
              <input
                type="text"
                aria-label="Search listings"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by title or description…"
                className="w-full pl-9 pr-4 py-2.5 border border-sand-200 rounded-lg text-sm text-ink-900 placeholder-ink-300 bg-white focus:outline-none focus:ring-2 focus:ring-terra-400 focus:border-transparent transition"
              />
            </div>

            <ItemFilterPanel
              isOpen={isFilterOpen}
              onToggle={() => setIsFilterOpen(v => !v)}
              categoryFilter={categoryFilter}
              setCategoryFilter={setCategoryFilter}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              statusOptions={STATUS_OPTIONS}
              sortDir={sortDir}
              setSortDir={setSortDir}
              sortOptions={SORT_OPTIONS}
              activeFilterCount={activeFilterCount}
              clearFilters={handleClearFilters}
            />

            {filteredItems.length === 0 ? (
              <div className="flex flex-col items-center py-12 gap-2">
                <p className="font-heading text-base font-semibold text-ink-900">No listings match your filters</p>
                <button type="button" onClick={handleClearFilters} className="text-sm text-terra-400 hover:text-terra-500 font-medium transition-colors">
                  Clear filters
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {pagedItems.map(l => (
                    <div key={l.id} className="relative group">
                      <ListingCard listing={l} />
                      <Link
                        to={`/listings/${l.id}/edit`}
                        aria-label={`Edit ${l.title}`}
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-white border border-sand-200 rounded-md px-3 py-1.5 text-xs font-medium text-ink-700 shadow-card"
                        onClick={e => e.stopPropagation()}
                      >
                        Edit
                      </Link>
                    </div>
                  ))}
                </div>
                <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
              </>
            )}
          </>
        )}

      </div>
    </div>
  );
}
